import type { CombinedError } from '@urql/core';
import { retryExchange } from '@urql/exchange-retry';

import { logWarning } from '@utils/miscUtils';

const RETRYABLE_STATUS_CODES = new Set([429, 500, 502, 503, 504]);

export const shouldRetryContentfulError = (error: CombinedError) =>
	!!error.networkError || RETRYABLE_STATUS_CODES.has(error.response?.status);

interface RetryCircuitBreakerOptions {
	failureThreshold: number;
	windowMs: number;
	cooldownMs: number;
	now?: () => number;
}

// Stops retries once `failureThreshold` retryable failures happen within `windowMs`, for `cooldownMs`. During a real
// Contentful outage this fails fast instead of multiplying load by `maxNumberAttempts`. After the cooldown retries
// resume; if Contentful is still down the breaker trips again.
export const createRetryCircuitBreaker = ({
	failureThreshold,
	windowMs,
	cooldownMs,
	now = Date.now,
}: RetryCircuitBreakerOptions) => {
	let failureTimestamps: number[] = [];
	let openUntil = 0;

	return {
		allowRetry() {
			const timestamp = now();
			if (timestamp < openUntil) return false;

			failureTimestamps = failureTimestamps.filter((failedAt) => timestamp - failedAt < windowMs);
			failureTimestamps.push(timestamp);

			if (failureTimestamps.length >= failureThreshold) {
				openUntil = timestamp + cooldownMs;
				failureTimestamps = [];
				logWarning(
					`CONTENTFUL_RETRY_CIRCUIT_OPEN: ${failureThreshold} failures in ${windowMs}ms, retries paused for ${cooldownMs}ms`
				);
				return false;
			}

			return true;
		},
	};
};

const contentfulRetryCircuitBreaker = createRetryCircuitBreaker({
	failureThreshold: 10,
	windowMs: 10_000,
	cooldownMs: 30_000,
});

// Retry connection failures ("fetch failed") and transient Contentful responses. Place it above the persisted
// exchange so a retried operation still goes through the persisted query handshake.
export const contentfulRetryExchange = retryExchange({
	initialDelayMs: 200,
	maxDelayMs: 2_000,
	randomDelay: true,
	maxNumberAttempts: 3,
	retryIf: (error) => shouldRetryContentfulError(error) && contentfulRetryCircuitBreaker.allowRetry(),
});
